'use client';

import React, { useCallback, useState } from 'react';

import Link from 'next/link';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import MuiLink from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import useSWR from 'swr';

import { ThreadFeedItem } from '@/components/FeedItem';
import { EXAMPLE_TOPICS } from '@/components/OmniSearchBox';
import { SuggestedFollows } from '@/components/SuggestedFollows';
import { WelcomeCard } from '@/components/WelcomeCard';
import { InfoCard } from '@/components/cards';
import { useUserInfo } from '@/context/UserInfoContext';
import { useFeedSummaryPolling } from '@/hooks/useFeedSummaryPolling';
import { API_URL } from '@/lib/consts';
import { topic_url } from '@/lib/utils';
import { PAGE_PY } from '@/theme/layoutConstants';

import { DisplayConceptRef, urlFetcher, FeedItem, DisplayUser } from '../lib/api';

const Home: React.FC = () => {
  const { userInfo, reloadUserInfo } = useUserInfo();
  const [numItems, setNumItems] = useState(10);
  const [allFeedItems, setAllFeedItems] = useState<FeedItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);

  // Check if we should show the welcome card
  React.useEffect(() => {
    setShowWelcomeCard(userInfo ? !userInfo.has_been_welcomed : false);
  }, [userInfo]);

  // Use the public feed endpoint for non-logged-in users, otherwise use the personalized feed
  const endpoint = userInfo ? 'unified' : 'public';

  // This will fetch all feed items up to the specified number
  const { data, error, isLoading } = useSWR<FeedItem[]>(
    `${API_URL}/feed/${endpoint}/${numItems}`,
    urlFetcher,
    {
      // Don't revalidate on focus to prevent unexpected refreshes
      revalidateOnFocus: false,
      onSuccess: (newData) => {
        if (newData) {
          setAllFeedItems(newData);
          setIsLoadingMore(false);
        }
      },
    }
  );

  // Function to load more items
  const handleLoadMoreUnifiedFeed = () => {
    // Prevent multiple simultaneous requests
    if (isLoadingMore) return;

    setIsLoadingMore(true);
    // Increment the number of items to fetch by 10
    setNumItems((prev) => prev + 10);
  };

  // Enable summary polling for feed items without summaries
  useFeedSummaryPolling({
    feedItems: allFeedItems,
    feedEndpoint: `${endpoint}/${numItems}`,
    enabled: !isLoading && !error && allFeedItems.length > 0,
  });

  // Use EXAMPLE_TOPICS for non-logged-in users, or combine with user topics if logged in
  const example_topics = userInfo
    ? userInfo.followed_topics
        .filter((t) => t.display)
        .concat(EXAMPLE_TOPICS)
        .slice(0, 5)
    : EXAMPLE_TOPICS.slice(0, 5);

  return (
    <Container maxWidth="md" sx={{ py: PAGE_PY.content }}>
      {/* Show welcome card for logged-in users who haven't been welcomed */}
      {showWelcomeCard && (
        <WelcomeCard
          onDismiss={() => {
            setShowWelcomeCard(false);
            reloadUserInfo(); // Reload user info to update has_been_welcomed status
          }}
        />
      )}

      {/* Content feed section - available to all users */}
      <Box component="section" sx={{ width: '100%', mb: 3 }} aria-labelledby="unified-feed">
        <h2 id="unified-feed">{userInfo ? 'Your Feed' : 'Product Reviews and Discussion'}</h2>
        {allFeedItems && allFeedItems.length > 0 ? (
          <Stack spacing={2}>
            {allFeedItems.map((item, index) => (
              <ThreadFeedItem
                key={index}
                item={item}
                showTopic={true}
                currentUser={userInfo || undefined}
              />
            ))}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                onClick={handleLoadMoreUnifiedFeed}
                aria-label="Load more content"
                disabled={isLoadingMore}
                variant="outlined"
                size="medium"
              >
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          </Stack>
        ) : isLoading ? (
          <Stack spacing={2}>
            {[...Array(5)].map((_, index) => (
              <Box key={index}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 1 }} />
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" />
              </Box>
            ))}
          </Stack>
        ) : error ? (
          <p>Error loading content. Please try again later.</p>
        ) : (
          <p>
            Nothing to see! {!userInfo && <Link href="/login">Log in</Link>} to see personalized
            content.
          </p>
        )}
      </Box>

      {userInfo && (
        <Box component="section" sx={{ width: '100%', mb: 3 }} aria-labelledby="topics-and-people">
          <h2 id="topics-and-people">Topics and People</h2>
          <Stack spacing={3}>
            <InfoCard title="Followed Topics">
              {userInfo.followed_topics && userInfo.followed_topics.length > 0 ? (
                <Stack component="nav" spacing={1} aria-label="Followed topics">
                  {userInfo.followed_topics.map((topic: DisplayConceptRef, index: number) => (
                    <MuiLink
                      key={index}
                      component={Link}
                      href={topic_url(topic.cref)}
                      underline="none"
                      color="primary"
                    >
                      {topic.display}
                    </MuiLink>
                  ))}
                </Stack>
              ) : (
                <p>You have no followed topics yet.</p>
              )}
            </InfoCard>

            {/* <ContentCard title="Your Follows" collapsible={false}>
            {userInfoLoading ? (
              <p>Loading your follows...</p>
            ) : userInfoError ? (
              <p>Error loading your follows.</p>
            ) : userInfo && userInfo.following.length > 0 ? (
              <ul className="no-bullets">
                {userInfo.following.map((follow: Follow, index: number) => (
                  <li key={index}>
                    <Link href={`/citizen/${encodeURIComponent(follow.username)}`}>
                      {follow.username}
                    </Link>
                    {follow.topics.length > 0 && (
                    <div>Topics: {follow.topics.map((c: DisplayConceptRef) => c.display).join(', ')} </div> ) }
                    <FollowedUserPosts username={follow.username} />
                  </li>
                ))}
              </ul>
            ) : (
              <p>You are not following anyone yet.</p>
            )}
          </ContentCard> */}

            {/* <ContentCard title="Your Recent Posts" collapsible={false}>
            {postsLoading ? (
              <p>Loading your posts...</p>
            ) : postsError ? (
              <p>Error loading your posts.</p>
            ) : posts && posts.length > 0 ? (
              <UserPosts posts={posts} />
            ) : (
              <p>You have no posts.</p>
            )}
          </ContentCard> */}

            <InfoCard title="You might also know...">
              <SuggestedFollows userInfo={userInfo} reloadUserInfo={reloadUserInfo} />
            </InfoCard>
          </Stack>
        </Box>
      )}
    </Container>
  );
};

export default Home;
